/*
 * Copyright (c) 2022. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import abstractCaseTranslations from "./abstractCaseTranslations";
import abstractLitigationCaseTranslations from "./abstractLitigationCaseTranslations";
import personTranslations from "./personTranslations";
import organizationTranslations from "./organizationTranslations";

const trafficTranslations = {
  ...abstractCaseTranslations,
  ...abstractLitigationCaseTranslations,
  label: "Verkehrsunfall",
  typeOfDamage: {
    label: "Schadenart",
    question: "Welche Art von Schaden haben Sie durch den Unfall erlitten?",
    options: {
      vehicle: {
        label: "Schaden am Fahrzeug",
        optionHintText: "Umfasst jeglichen Schaden an Ihrem Fahrzeug, wie z. B. Dellen, Kratzer, Totalschaden.",
      },
      person: {
        label: "Personenschaden",
        optionHintText:
          "Bezeichnet alle gesundheitlichen Beeinträchtigungen, die Sie oder andere Personen in Ihrem Fahrzeug infolge des Unfalls erlitten haben. Dazu gehören z. B. Prellungen, Schürfwunden, Knochenbrüche, innere Verletzungen oder psychische Schäden.",
      },
      vehicleAndPerson: {
        label: "Schaden am Fahrzeug und Personenschaden",
      },
      other: {
        label: "Anderer Sachschaden",
        optionHintText: "Betrifft ausschließlich beschädigte Gegenstände, wie z.B. Zäune, Mauern, Kleidung, elektronische Geräte oder transportiertes Gepäck.",
      },
    },
  },
  typeOfAccident: {
    question: "Um welche Art von Unfall handelt es sich?",
    label: "Unfallart",
    options: {
      rearEndCollision: { label: "Auffahrunfall" },
      laneChangeCollision: { label: "Spurwechselkollision" },
      parking: { label: "Ein- / Ausparken" },
      other: { label: "Sonstiges / Unklar" },
    },
  },
  clientIsVehicleOwner: {
    question: "Sind Sie selbst Halter und/oder Eigentümer des beschädigten Fahrzeugs?",
    options: {
      yes: { label: "Ja" },
      no: { label: "Nein" },
    },
  },
  hasPreciseAccidentAddress: {
    question: "Können Sie eine genaue Adresse zum Unfallort angeben?",
    options: {
      yes: { label: "Ja, ich kann Straße und Ort benennen" },
      no: { label: "Nein, der Ort verfügt über keine eindeutige Adresse" },
    },
  },
  driverOfClientVehicle: {
    question: "Wer war zum Unfallzeitpunkt Fahrer Ihres Fahrzeugs?",
    label: "Wer war zum Unfallzeitpunkt Fahrer Ihres Fahrzeugs?",
    options: {
      client: { label: "Ich selbst" },
      other: { label: "Eine andere Person" },
      nobody: { label: "Niemand (Fahrzeug geparkt)" },
    },
  },
  opposingPartyType: {
    question: "Ist das Fahrzeug des Unfallgegners auf eine Privatperson oder auf ein Unternehmen zugelassen?",
    options: {
      opposingPartyPerson: { label: "Privatperson" },
      opposingPartyLegalEntity: { label: "Unternehmen" },
      null: { label: "Mir liegen keine Informationen vor" },
    },
  },
  damageEstimationDocumentType: {
    question: "Welches Dokument zur Bestimmung der Schadenhöhe liegt Ihnen bereits vor?",
    label: "Dokument zur Bestimmung der Schadenshöhe",
    options: {
      costEstimate: { label: "Kostenvoranschlag" },
      assessment: { label: "Gutachten" },
      repairInvoice: { label: "Reparaturrechnung" },
      noDocument: { label: "Noch keines" },
    },
  },
  clientBankData: {
    iban: {
      question: "Wie lautet Ihre IBAN, auf die der Schadenersatz von der Gegenseite überwiesen werden soll?",
      helperText:
        "Sollte Ihnen Ihre IBAN gerade nicht vorliegen, lassen Sie das Feld zunächst leer. Wir benötigen die IBAN jedoch spätestens für unser Schreiben an den gegnerischen Haftpflichtversicherer.",
      label: "IBAN",
    },
  },
  courseOfEvents: {
    question: "Bitte beschreiben Sie den Unfallhergang",
    label: "Unfallhergang",
  },
  accidentLocalityDescription: {
    question: "Bitte beschreiben Sie den Unfallort",
    label: "Beschreibung des Unfallorts",
    helperText: "Beschreiben Sie bitte möglichst genau, wo sich der Unfall ereignet hat, z.B. auf welchem Autobahnabschnitt oder auf welchem Parkplatz.",
  },
  accidentDate: {
    question: "Wann hat sich der Unfall ereignet?",
    label: "Datum des Unfalls",
  },
  accidentTime: {
    question: "Um welche Uhrzeit hat sich der Unfall ereignet?",
    label: "Unfallzeitpunkt",
  },
  descriptionInjuries: {
    question: "Welche Verletzungen oder Beeinträchtigungen haben Sie bei dem Unfall erlitten?",
    label: "Beschreibung Personenschaden",
  },
  descriptionDamage: {
    question: "Welche Gegenstände sind in welchem Umfang beschädigt wurden?",
    label: "Beschreibung Sachschaden",
  },
  opposingCarLiabilityInsurance: {
    legalName: {
      label: "KFZ-Haftpflichtversicherer Unfallgegner",
    },
    label: "KFZ-Haftpflicht Unfallgegner",
    question: "Über welchen Versicherer ist das Fahrzeug des Unfallgegners haftpflichtversichert?",
  },
  vehicleModel: {
    question: "Wie heißt das Modell des Fahrzeugs?",
    label: "Modell",
  },
  vehicleId: {
    question: "Wenn sie Ihnen gerade vorliegt, geben Sie bitte die Fahrzeug-Identifikationsnummer an - Sie finden diese im Fahrzeugbrief",
    label: "Fahrzeug-Identifikationsnummer",
  },
  opposingInsurancePolicyNumber: {
    question: "Wie lautet die Versicherungsnummer des Unfallgegners bei der KFZ-Haftpflichtversicherung?",
    label: "Versicherungsnummer Unfallgegner",
  },
  opposingCaseReferenceNumber: {
    question: "Wie lautet die Schadennummer bei der KFZ-Haftpflichtversicherung des Unfallgegners?",
    label: "Schadennummer Unfallgegner",
  },
  licensePlateOpposingParty: {
    question: "Wie lautet das amtliche Kennzeichen des Unfallgegners?",
    label: "KFZ-Kennzeichen Unfallgegner",
  },
  licensePlateClientVehicle: {
    question: "Bitte geben Sie das amtliche Kennzeichen Ihres Fahrzeugs an",
    label: "KFZ-Kennzeichen eigenes Fahrzeug",
  },
  vehicleMake: {
    question: "Bitte wählen Sie die Marke Ihres beschädigten Fahrzeugs aus",
    label: "Marke",
  },
  accidentAddress: {
    streetAddress: {
      label: "Straße und Hausnummer Unfallort",
    },
    postalCode: {
      label: "PLZ Unfallort",
    },
    addressLocality: {
      label: "Ort Unfallort",
    },
  },
  driverPerson: {
    gender: {
      label: "Anrede Fahrer",
      options: {
        male: { label: "Herr" },
        female: { label: "Frau" },
        diverse: { label: "Divers" },
      },
    },
    givenName: {
      label: "Vorname Fahrer",
    },
    familyName: {
      label: "Nachname Fahrer",
    },
    residenceAddress: {
      streetAddress: {
        label: "Straße und Hausnummer Fahrer",
      },
      postalCode: {
        label: "PLZ Fahrer",
      },
      addressLocality: {
        label: "Ort Fahrer",
      },
    },
  },
  opposingPartyPerson: personTranslations,
  opposingPartyLegalEntity: organizationTranslations,
};

export default trafficTranslations;
